import {
  type DtoApplication,
  type DtoApplicationSummaryResp,
  type DtoApplicationSummaryUpdateReq,
  type DtoCreateInvitedCoBorrowerReq,
  type DtoUpdateApplicationReq,
} from "@porchpass/api-client";
import { safeNumber } from "src/utils";
import { requiredField } from "./required";

export type LoanOverviewFormData = {
  home_price?: string;
  home_upgrades_cost?: string;
  site_improvement_amount?: string;
  land_purchase_or_payoff_amount?: string;
  loan_interest_rate?: string;
  home_insurance_rate?: string | null;
  property_tax_rate?: string | null;
  down_payment_amount?: string;
  loan_total_amount?: string;
  estimated_monthly_payment?: string | null;
  loan_interest_monthly_rate?: string | null;
  loan_years?: string;
  loan_months?: string;
  home_insurance_monthly_payment?: string;
  property_tax_monthly_payment?: string;
  principal_plus_interest_monthly_payment?: string;
};

export type SalesPersonFormData = {
  sales_rep_name?: string;
  sales_rep_email?: string;
};

export type CoborrowerInviteFormData = {
  email?: string;
  phone_number?: string;
  ignore_duplicate?: boolean;
};

export function fromApplicationSummary(
  data: DtoApplicationSummaryResp,
): LoanOverviewFormData {
  return {
    home_price: data.home_price?.toString(),
    home_upgrades_cost: data.home_upgrades_cost?.toString(),
    site_improvement_amount: data.site_improvement_amount?.toString(),
    land_purchase_or_payoff_amount:
      data.land_purchase_or_payoff_amount?.toString(),
    loan_interest_rate: data.loan_interest_rate?.toString(),
    home_insurance_rate: data.home_insurance_rate?.toString() ?? null,
    property_tax_rate: data.property_tax_rate?.toString() ?? null,
    down_payment_amount: data.down_payment_amount?.toString(),
    loan_total_amount: data.loan_total_amount?.toString(),
    estimated_monthly_payment:
      data.estimated_monthly_payment?.toString() ?? null,
    loan_interest_monthly_rate:
      data.loan_interest_monthly_rate?.toString() ?? null,
    loan_years: data.loan_years?.toString(),
    loan_months: data.loan_months?.toString(),
    home_insurance_monthly_payment:
      data.home_insurance_monthly_payment?.toString(),
    property_tax_monthly_payment: data.property_tax_monthly_payment?.toString(),
    principal_plus_interest_monthly_payment:
      data.principal_plus_interest_monthly_payment?.toString(),
  };
}

export function toApplicationSummaryUpdateReq(
  data: LoanOverviewFormData,
): DtoApplicationSummaryUpdateReq {
  return {
    home_price: safeNumber(data.home_price),
    home_upgrades_cost: safeNumber(data.home_upgrades_cost),
    site_improvement_amount: safeNumber(data.site_improvement_amount),
    land_purchase_or_payoff_amount: data.land_purchase_or_payoff_amount
      ? safeNumber(data.land_purchase_or_payoff_amount)
      : undefined,
    loan_interest_rate: safeNumber(data.loan_interest_rate),
    home_insurance_rate:
      data.home_insurance_rate === null
        ? undefined
        : safeNumber(data.home_insurance_rate),
    property_tax_rate:
      data.property_tax_rate === null
        ? undefined
        : safeNumber(data.property_tax_rate),
    down_payment_amount: safeNumber(data.down_payment_amount),
  };
}

export function fromSalesPerson(data: DtoApplication): SalesPersonFormData {
  return {
    sales_rep_name: data.sales_rep_name || "",
    sales_rep_email: data.sales_rep_email || "",
  };
}

export function toSalesPersonUpdateReq(
  data: SalesPersonFormData,
): DtoUpdateApplicationReq {
  return {
    sales_rep_name: requiredField(data.sales_rep_name, "sales_rep_name"),
    sales_rep_email: data.sales_rep_email?.length
      ? data.sales_rep_email
      : undefined,
  };
}

export function toCoborrowerInviteReq(
  data: CoborrowerInviteFormData,
): DtoCreateInvitedCoBorrowerReq {
  return {
    email: data.email,
    phone_number: data.phone_number,
    ...(data.ignore_duplicate ? { ignore_duplicate: true } : {}),
  };
}
